import classNames from "classnames";
import * as React from "react";

import { type MessagePathNode } from "@/shared/domain/topics";

import { useTopicTreeForFile, useWorkspaceFiles } from "../../WorkspaceCtx";

import styles from "./DataSourceSelector.module.css";
import { FileSelector } from "./FileSelector";
import { type PathSelectorProps, PathSelector } from "./PathSelector";

interface DataSourceSelectorProps {
  /** Optional class name to apply to the root element */
  className?: classNames.Argument;

  /** Initial value used in the combo box for specifying file */
  initialFile?: string;

  /** Initial value used in the input box for specifying topic and message path */
  initialPath?: string;

  /** Callback when a valid, matched `MessagePathNode` has been selected. */
  onChange: PathSelectorProps["onChange"];

  /** Called when the user clears the current selection. */
  onClear?: PathSelectorProps["onClear"];

  /**
   * Called for each candidate path match suggestion.
   * If it returns false, the path match suggestion is discarded.
   * Primary use case is to prune options that are inappropriate for a given panel type.
   * E.g., only show message path options that are bytes-like when selecting a message path
   * for the image panel.
   */
  optionFilter?: (messagePath: MessagePathNode) => boolean;

  /**
   * Optional comparator used to sort path match suggestions.
   * Applied as a secondary sort that preserves the default ordering
   * (complete matches first, then incomplete matches).
   */
  optionSort?: (a: MessagePathNode, b: MessagePathNode) => number;
}

export function DataSourceSelector({
  className,
  initialFile,
  initialPath,
  onChange,
  onClear,
  optionFilter,
  optionSort,
}: DataSourceSelectorProps) {
  const availableFiles = useWorkspaceFiles();
  const [selectedFile, setSelectedFile] = React.useState<string | undefined>(
    () => {
      return initialFile ?? availableFiles[0]?.file_id;
    },
  );
  const options = useTopicTreeForFile(selectedFile);

  const [shouldFocusPathSelector, setShouldFocusPathSelector] =
    React.useState(false);
  const pathSelectorRef = React.useRef<HTMLElement | null>(null);

  const onSelectFile = React.useCallback((fileId: string) => {
    setSelectedFile(fileId);
    setShouldFocusPathSelector(true);
  }, []);

  /**
   * Focus and open the path selector input when file selection changes.
   * Resets focus flag after execution.
   */
  React.useEffect(
    function focusPathSelectorOnFileChange() {
      const pathSelector = pathSelectorRef.current;
      if (shouldFocusPathSelector && pathSelector !== null) {
        const input = pathSelector.querySelector("input");
        if (input) {
          input.focus();
          input.click();
        }
        setShouldFocusPathSelector(false);
      }
    },
    [shouldFocusPathSelector],
  );

  return (
    <div className={classNames(className)}>
      <FileSelector
        className={styles.selector}
        onSelect={onSelectFile}
        selection={selectedFile}
      />
      <PathSelector
        className={styles.selector}
        initialInputValue={initialPath}
        onChange={onChange}
        onClear={onClear}
        options={options}
        optionFilter={optionFilter}
        optionSort={optionSort}
        ref={pathSelectorRef}
      />
    </div>
  );
}
